import { Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Observable } from "rxjs";
import {
  DataLicense,
  FileResource,
  Submission,
  SubmissionControllerService
} from "../../../../modules/lipidcompass-backend-client";
import { submissionWorkflowConfig } from "../../../environments/environment";
import { LoggingService, LogLevel } from "../../_services/logging.service";
import { datasetsOf } from "../../_services/submission-datasets.util";
import { CardModule } from "primeng/card";

import { TableModule } from "primeng/table";
import { ButtonGroupModule } from "primeng/buttongroup";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";

@Component({
  selector: "app-validation",
  templateUrl: "./validation.component.html",
  styleUrls: ["./validation.component.scss"],
  standalone: true,
  imports: [
    ButtonModule,
    ButtonGroupModule,
    CardModule,
    DialogModule,
    TableModule
  ]
})
export class ValidationComponent implements OnInit {
  private submissionService = inject(SubmissionControllerService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private loggingService = inject(LoggingService);

  submissionId: string;
  activeIndex: number = 3;
  submission: Submission;
  public progress: boolean;
  validationMessages: any = {};
  level: any;
  maxErrors: 30;
  semanticValidation: true;
  mzTabFiles: FileResource[];
  hasMzTabMfiles: false;
  errorCount: number = 0;
  strictValidation: boolean = submissionWorkflowConfig.strictValidation;
  editMzTab: boolean = false;
  editMzTabFile: FileResource;

  ngOnInit() {
    this.submissionId = this.route.snapshot.params.id;
    this.loadSubmission(this.submissionId);
  }

  loadSubmission(submissionId: string) {
    this.submissionService.submissionGetById(submissionId).subscribe(result => {
      this.submission = <Submission>result;
      if (this.submission) {
        this.loggingService.log(
          "ValidationComponent",
          "Found the following files: " +
            JSON.stringify(this.submission.submittedFiles),
          LogLevel.DEBUG
        );
        // One row per DATASET, not one per mzTab-M file. Listing the submitter's
        // ORIGINAL and the platform's derived WORKING copy as two separate
        // validatable rows is what made validating the ORIGINAL a 500 and left
        // allFilesValid() permanently false (#365).
        this.mzTabFiles = datasetsOf(this.submission?.submittedFiles);
        this.mzTabFiles.map(value => {
          this.validationMessages[value.fileName] = value.validationMessages;
        });
        this.loggingService.log(
          "ValidationComponent",
          "Found the following mzTab datasets: " + JSON.stringify(this.mzTabFiles),
          LogLevel.DEBUG
        );
        this.progress = false;
      }
    });
  }

  validate(fileResource: FileResource) {
    this.loggingService.log(
      "ValidationComponent",
      "Validating file: " + JSON.stringify(fileResource.fileName),
      LogLevel.DEBUG
    );
    this.progress = true;
    this.submissionService
      .submissionValidateMzTabFile(this.submissionId, fileResource)
      .subscribe(
        response => {
          this.loadSubmission(this.submissionId);
        },
        error => {
          this.loggingService.log(
            "ValidationComponent",
            "Received error response: " + JSON.stringify(error),
            LogLevel.DEBUG
          );
          this.loadSubmission(this.submissionId);
        }
      );
  }

  checkWasValidated(fileResource: FileResource) {
    if (fileResource.lastValidated) {
      let lastValidated: Date = new Date(fileResource.lastValidated);
      if (lastValidated.toISOString() === "1970-01-01T00:00:00.000Z") {
        this.loggingService.log(
          "ValidationComponent",
          "File has never been validated: " + lastValidated.toISOString(),
          LogLevel.DEBUG
        );
        return false;
      }
      return true;
    } else {
      return false;
    }
  }

  checkAllFilesWereValidated() {
    return this.mzTabFiles.every(value => this.checkWasValidated(value));
  }

  isValid(fileResource: FileResource) {
    let wasValidated = this.checkWasValidated(fileResource);
    if (!wasValidated) {
      return false;
    }
    let hasErrors: boolean = this.validationMessages[
      fileResource.fileName
    ]?.some(value => value.level === "ERROR");
    let hasWarnings: boolean = this.validationMessages[
      fileResource.fileName
    ]?.some(value => value.level === "WARN");
    if (this.strictValidation) {
      return !hasErrors && !hasWarnings;
    }
    return !hasErrors;
  }

  allFilesValid() {
    return this.mzTabFiles?.every(value => this.isValid(value));
  }

  hasValidationMessages(fileResource: FileResource) {
    return this.validationMessages[fileResource.fileName]?.length > 0;
  }

  getValidationMessageLevelCount(fileResource: FileResource, level: string) {
    return this.validationMessages[fileResource.fileName]?.filter(
      value => value.level === level
    ).length;
  }

  edit(fileResource: FileResource) {
    this.editMzTabFile = fileResource;
    this.editMzTab = true;
  }

  cancelEdit() {
    this.editMzTabFile = null;
    this.editMzTab = false;
  }

  saveEdit() {
    if (!this.editMzTabFile.dataLicense) {
      this.loggingService.log(
        "ValidationComponent",
        "No data license found for file: " + this.editMzTabFile.fileName,
        LogLevel.ERROR
      );
      return;
    }
    const dataLicense: DataLicense = this.editMzTabFile.dataLicense;
    const fileResource: FileResource = this.editMzTabFile;
    if (!fileResource.filePath) {
      this.loggingService.log(
        "ValidationComponent",
        "No file path found for file: " + fileResource.fileName,
        LogLevel.ERROR
      );
      return;
    }
    const blob = new Blob([fileResource.filePath], {
      type: fileResource.fileType
    });
    this.submissionService
      .submissionUpload(this.submissionId, dataLicense, blob)
      .subscribe(
        response => {
          this.loadSubmission(this.submissionId);
        },
        error => {
          this.loggingService.log(
            "ValidationComponent",
            "Received error response: " + JSON.stringify(error),
            LogLevel.DEBUG
          );
          this.loadSubmission(this.submissionId);
        }
      );
  }

  nextPage(event: any) {
    this.router.navigate(
      [
        submissionWorkflowConfig.workflows.mzTab.steps[this.activeIndex]
          .routerLink,
        this.submissionId
      ],
      { relativeTo: this.route.parent }
    );
    return;
  }

  previousPage(event: any) {
    this.loggingService.log(
      "ValidationComponent",
      "previousPage: " + JSON.stringify(event),
      LogLevel.DEBUG
    );
    if (this.activeIndex > 1) {
      this.loggingService.log(
        "ValidationComponent",
        "previousPage via activeIndex: " +
          JSON.stringify([
            submissionWorkflowConfig.workflows.mzTab.steps[this.activeIndex - 2]
              .routerLink,
            this.submissionId
          ]),
        LogLevel.DEBUG
      );
      this.router.navigate(
        [
          submissionWorkflowConfig.workflows.mzTab.steps[this.activeIndex - 2]
            .routerLink,
          this.submissionId
        ],
        { relativeTo: this.route.parent }
      );
    } else {
      this.loggingService.log(
        "ValidationComponent",
        "previousPage to parent: " + JSON.stringify([this.route.parent]),
        LogLevel.DEBUG
      );
      this.router.navigate(submissionWorkflowConfig.parentRoute);
    }
    return;
  }

  cancel(event: any) {
    this.router.navigate(submissionWorkflowConfig.parentRoute);
    return;
  }
}
